import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { SupplierDocument, UploadSupplierDocumentPayload } from './types';
import { supplierDocumentsQueryKey } from './use-get-supplier-documents-api';

const getEndpoint = (supplierProfileId: number) => `ums/v1/cms/supplier-profiles/${supplierProfileId}/documents`;

type TData = SupplierDocument;
type TError = AxiosError;
type TVariables = UploadSupplierDocumentPayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** BE errors to surface verbatim: 400 missing customLabel for OTHER, 404 unknown profile,
 * 422 the file is missing, too large, or not a PDF/image. */
export function useUploadSupplierDocumentApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ supplierProfileId, type, customLabel, expiryDate, file }: TVariables) => {
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('type', type);
      if (customLabel) body.append('customLabel', customLabel);
      if (expiryDate) body.append('expiryDate', expiryDate);

      const { data } = await http.post(getEndpoint(supplierProfileId), body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: ['supplier-documents/upload'],
    onSuccess: (data, variables, context) => {
      toast({ description: 'Document uploaded successfully', status: 'success' });
      queryClient.invalidateQueries([supplierDocumentsQueryKey, variables.supplierProfileId]);
      if (userOnSuccess) userOnSuccess(data, variables, context);
    },
    ...restOptions,
  });
}
